// web/src/lib/trust.tsx — the honest, in-UI account of the zero-trust model
// (M5.4 / M5.1). The single source of the "what the server can see" and
// "how this works" copy, reused by the persistent top-bar trust indicator and
// anywhere the trust story is told. Copy is LOAD-BEARING and verbatim from
// design/content-guide.md §3 and §4 — it states what the server CANNOT do as
// plainly as what it can, and never implies the system can claw back plaintext
// (§4.5 / SPEC "Consequence accepted"). Presentation only; no material is read.
import React from 'react';

/** The collapsed top-bar label (content-guide §3.1). */
export const TRUST_COLLAPSED_LABEL = 'Server sees ciphertext only';

/** The aria-label / focus tooltip for the indicator trigger (content-guide §3.1). */
export const TRUST_ARIA_LABEL =
  'What the server can see — metadata and encrypted shards only, never your secret. Activate to learn more.';

/** The expanded panel title (content-guide §3.1). */
export const TRUST_PANEL_TITLE = 'What the server can see';

/**
 * The expanded "what the server can see" body (content-guide §3.1). Rendered as
 * structured prose so the "can see" / "can never see" split stays scannable.
 */
export function WhatTheServerCanSee(): React.JSX.Element {
  return (
    <div className="bgTrustBody">
      <p>
        Break Glass is built so that <strong>no server — not even ours — can read your
        secret.</strong>
      </p>
      <p>
        <span className="bgTrustHeading">The server can see:</span> the record&apos;s name and
        description, who its members are, the quorum rule (K of N), timestamps, and the audit
        trail. It also relays <strong>encrypted shards</strong> it cannot open.
      </p>
      <p>
        <span className="bgTrustHeading">The server can never see:</span> your secret, any shard
        in the clear, or any key that could reconstruct it. Your secret only exists in plain text
        in two places, and only for a moment: the initiator&apos;s browser when it&apos;s sealed,
        and the requester&apos;s browser when the glass is broken.
      </p>
    </div>
  );
}

/** One progressive-disclosure section in the "How this works" explainer. */
export interface ExplainerSection {
  id: string;
  /** Always-visible one-line summary (content-guide §4 "summary"). */
  summary: string;
  /** Expander label (content-guide §4 "How … works"). */
  expanderLabel: string;
  /** The honest detail (content-guide §4 expander body). */
  detail: React.ReactNode;
}

/**
 * The "How this works" explainer (content-guide §4): plain language first, an
 * optional expander per topic for the curious. These cover the splitting model,
 * where private keys live, and — mandatory wherever a window is shown (§4.5) —
 * the honest, weakened time-box. Strings are verbatim; the generic ${N}/${K}/
 * ${duration} placeholders are stated in their general form here (the per-record
 * surfaces interpolate concrete values).
 */
export const TRUST_EXPLAINER: readonly ExplainerSection[] = [
  {
    id: 'splitting',
    summary: 'Your secret is split into shards. Any quorum of them can rebuild it; fewer can’t.',
    expanderLabel: 'How splitting works',
    detail: (
      <>
        <p>
          Instead of storing your secret somewhere it could be read, Break Glass splits it into
          shards and gives one to each member. You choose how many shards are needed to rebuild it
          — the <strong>quorum</strong>, K of N.
        </p>
        <p>
          Any K members acting together can reconstruct the secret. Any <strong>K minus one</strong>{' '}
          learn <strong>nothing at all</strong> — not a partial secret, not a hint. This is a
          mathematical property (Shamir&apos;s Secret Sharing), not a policy we promise to enforce.
        </p>
        <p>
          The splitting happens entirely in your browser. The server only ever sees the encrypted
          shards.
        </p>
      </>
    ),
  },
  {
    id: 'keys',
    summary: 'Your private key is created on this device and never leaves it.',
    expanderLabel: 'How your keys work',
    detail: (
      <>
        <p>
          You have two keys: a <strong>public key</strong> (which you share, so others can encrypt
          a shard for you) and a <strong>private key</strong> (which only you hold, and which
          unlocks your shard).
        </p>
        <p>
          Your private key is generated here, in your browser, and stored so that it{' '}
          <strong>can&apos;t be copied off this device</strong> — not by you, not by us, not by the
          server. If you use a hardware key (like a YubiKey), it adds a second lock on top.
        </p>
        <p>
          Because the server only ever has your <em>public</em> key and your <em>encrypted</em>{' '}
          shard, it can&apos;t open your shard even if it wanted to.
        </p>
      </>
    ),
  },
  {
    id: 'window',
    summary:
      'A window controls how long this view stays open — but you’ve already seen the secret.',
    expanderLabel: 'What the window does (and doesn’t) do',
    detail: (
      <>
        <p>
          Once the glass is broken, the secret is in <strong>your</strong> browser. The window
          controls how long Break Glass keeps showing it to you here and how long the shards stay
          available to gather — it <strong>cannot</strong> reach into your browser and erase what
          you&apos;ve already seen.
        </p>
        <p>
          Treat a broken-glass secret as exposed: if it was a credential, <strong>rotate it</strong>{' '}
          once the emergency is over. The audit trail records that the glass was broken, by whom,
          and when.
        </p>
      </>
    ),
  },
  {
    id: 'lost-device',
    summary: 'Lost a device? Its shard can’t be recovered — re-deal to restore a healthy margin.',
    expanderLabel: 'Lost device and re-deal',
    detail: (
      <>
        <p>
          A shard lives only on the device that holds it. If a device or hardware key is lost, that
          shard can&apos;t be recovered — but the secret is safe as long as a quorum of other
          members can still act.
        </p>
        <p>
          To restore a healthy margin, the responsible party runs a <strong>re-deal</strong>: the
          secret is split again from a fresh copy and every current shard is revoked. Members
          receive new shards; the old ones stop working. The full change is recorded in the audit
          trail.
        </p>
      </>
    ),
  },
] as const;

// ---------------------------------------------------------------------------
// Least-privilege capability framing (M5.1). What you can / can't do, by role —
// stated plainly so affordances are legible. These describe the product's policy
// model (R15: ownership is accountability, not access; admins manage but cannot
// read secrets); they are not a live permission check, which the server enforces.
// ---------------------------------------------------------------------------

import type { Role } from '../../../shared/types';

export interface Capability {
  /** True = you can do this; false = you can't (least-privilege boundary). */
  allowed: boolean;
  text: string;
}

/**
 * The can / can't list for a role. The lines that hold for everyone (no one — not
 * even an admin — can read a secret without a quorum) come first, then role
 * specifics. Wording is calm and precise per the content guide.
 */
export function roleCapabilities(role: Role): readonly Capability[] {
  const everyone: Capability[] = [
    { allowed: true, text: 'Seal a new secret — its plaintext stays in this browser.' },
    {
      allowed: true,
      text: 'Hold a shard you’ve been given and submit it during an unseal.',
    },
    { allowed: false, text: 'Read a secret on your own — a quorum of members is always required.' },
    {
      allowed: false,
      text: 'Recover a lost shard — its device holds the only copy. Re-deal instead.',
    },
  ];
  if (role === 'admin') {
    return [
      ...everyone,
      { allowed: true, text: 'Manage members, policies, and re-deals.' },
      { allowed: true, text: 'Revoke a record and view the full audit trail.' },
      { allowed: false, text: 'Read any secret’s plaintext — admin manages, it does not decrypt.' },
    ];
  }
  if (role === 'approver') {
    return [
      ...everyone,
      { allowed: true, text: 'Be enrolled as a member and submit your shard during an unseal ceremony.' },
      { allowed: false, text: 'Manage members or policies — that’s an admin action.' },
    ];
  }
  // operator
  return [
    ...everyone,
    { allowed: true, text: 'Request a break-glass unseal on records you’re a member of.' },
    { allowed: false, text: 'Manage members, policies, or re-deals — that’s an admin action.' },
  ];
}

/** Reusable inline assurances (content-guide §3.2) for drop-in near sensitive surfaces. */
export const TRUST_INLINE = {
  nearSecretInput: 'This is encrypted in your browser before it leaves your device.',
  nearShards: "Shards are encrypted to each member. The server stores them but can't open them.",
  nearReveal:
    'This secret is now in your browser only. It was never sent to the server in plain text.',
  audit: 'The audit trail records that things happened — never the secrets themselves.',
} as const;
